// Shared chart palette so all dashboards stay visually consistent.
// Two mode-specific sets stepped for their surfaces (light: white card,
// dark: #181819 card). Both validated with the dataviz palette validator:
// lightness band, chroma floor, adjacent-pair CVD ΔE ≥ 8, normal-vision
// ΔE ≥ 15, and ≥ 3:1 contrast vs surface all PASS (2026-07-25).
//
// Use getChartColors()/getChartSeries()/getChartNeutrals() inside component
// render so charts pick up the active theme; the static exports are the
// light-mode values kept for backward compatibility.

export interface ChartColorSet {
    blue: string;
    green: string;
    amber: string;
    red: string;
    purple: string;
    slate: string;
    sky: string;
    indigo: string;
    cyan: string;
    pink: string;
    lime: string;
}

const LIGHT: ChartColorSet = {
    indigo: '#4f46e5',
    amber: '#d97706',
    green: '#0d9488',
    red: '#e11d48',
    purple: '#7c3aed',
    blue: '#0284c7',
    sky: '#0284c7',
    cyan: '#0e7490',
    pink: '#db2777',
    lime: '#65a30d',
    slate: '#64748b',
};

const DARK: ChartColorSet = {
    indigo: '#6366f1',
    amber: '#d97706',
    green: '#0d9488',
    red: '#f43f5e',
    purple: '#8b5cf6',
    blue: '#0891b2',
    sky: '#0891b2',
    cyan: '#0891b2',
    pink: '#ec4899',
    lime: '#84cc16',
    slate: '#94a3b8',
};

// Fixed categorical slot order — the ordering is the CVD-safety mechanism
// (validated as adjacent pairs); never re-sort or cycle extra hues past it.
const seriesOf = (c: ChartColorSet): string[] => [
    c.indigo, c.amber, c.green, c.red, c.purple, c.blue,
];

export interface ChartNeutralSet {
    axisLabel: string;
    gridLine: string;
    labelMuted: string;
    labelStrong: string;
    trackLight: string;
    trackMuted: string;
}

const NEUTRALS_LIGHT: ChartNeutralSet = {
    axisLabel: '#71717a',
    gridLine: '#f0f0f2',
    labelMuted: '#71717a',
    labelStrong: '#18181b',
    trackLight: '#e8e8ec',
    trackMuted: '#d4d4d8',
};

const NEUTRALS_DARK: ChartNeutralSet = {
    axisLabel: '#a1a1aa',
    gridLine: '#26262a',
    labelMuted: '#a1a1aa',
    labelStrong: '#f4f4f5',
    trackLight: '#2c2c30',
    trackMuted: '#3f3f46',
};

export const isDarkTheme = (): boolean =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

export const getChartColors = (): ChartColorSet => (isDarkTheme() ? DARK : LIGHT);
export const getChartSeries = (): string[] => seriesOf(getChartColors());
export const getChartNeutrals = (): ChartNeutralSet => (isDarkTheme() ? NEUTRALS_DARK : NEUTRALS_LIGHT);

// ── Static light-mode exports (backward compatibility) ──────────────────────
export const CHART_COLORS = LIGHT;
export const CHART_SERIES: string[] = seriesOf(LIGHT);
export const CHART_NEUTRALS = NEUTRALS_LIGHT;
